/**
 * Settings -> Import: bring the old Perfex CRM across, and take it back out
 * again without touching anything else.
 *
 * Gated on `manage:crm.import`, which is what every route behind it checks (see
 * `../perms.ts`). One key for the whole tab: there is no viewer half of this
 * screen worth splitting off - reading the source costs a connection to another
 * company's database, and the only reason to look is to decide whether to press
 * something.
 *
 * This file holds the data and the run, the cards beside it hold the drawing.
 * The reason the run lives here rather than in the card with the buttons is
 * that three cards depend on it: the progress table watches it, the history
 * gains a row when it ends, and the undo must not start while it is going.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, TriangleAlert } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getImportStaff,
  getImportStatus,
  introspectImport,
  isRunActive,
  listImportRuns,
  startImport,
  type ImportRun,
  type ImportRunDetail,
  type ImportStatus,
  type Introspection,
  type StaffReview,
} from "../../../../lib/crmImport";
import { Btn, Card, EmptyState, useToast } from "../../../ui";
import { ConnectionCard } from "./ConnectionCard";
import { EntityCard } from "./EntityCard";
import { RevertCard } from "./RevertCard";
import { RunHistory } from "./RunHistory";
import { RunProgress } from "./RunProgress";
import { SkippedCard } from "./SkippedCard";
import { StaffCard } from "./StaffCard";
import { useImportRun } from "./useImportRun";

export function ImportSettings() {
  const t = useT();
  const toast = useToast();

  const [status, setStatus] = useState<ImportStatus | null>(null);
  const [intro, setIntro] = useState<Introspection | null>(null);
  const [staff, setStaff] = useState<StaffReview | null>(null);
  const [runs, setRuns] = useState<ImportRun[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);

  // The tick-everything default is seeded once. A reload that re-ticked would
  // undo a deliberate choice - and the reload after a run is automatic, so the
  // operator would never see it happen.
  const seeded = useRef(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [next, history] = await Promise.all([getImportStatus(), listImportRuns()]);
      setStatus(next);
      setRuns(history);
      // Only ever adopted, never cleared here: a run that has just finished is
      // gone from `running` and is exactly the one the progress card should go
      // on showing.
      if (next.running) setActiveRunId(next.running.id);

      if (next.configured) {
        // Its own request and its own catch: the staff review opens a second
        // connection and reads five tables, and a failure there must not cost
        // the operator the entity list they came for.
        void getImportStaff()
          .then(setStaff)
          .catch(() => setStaff(null));
        try {
          const schema = await introspectImport();
          setIntro(schema);
          if (!seeded.current) {
            seeded.current = true;
            setSelected(new Set(schema.entities.filter((e) => e.table !== null).map((e) => e.entity)));
          }
        } catch {
          // The schema read is the expensive half and the one that fails when
          // the tunnel is down. Losing it must not take the connection card and
          // the run history with it.
          setIntro(null);
        }
      } else {
        setIntro(null);
      }
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onFinished = useCallback(
    (run: ImportRunDetail) => {
      void load();
      toast(
        t(
          run.status === "failed"
            ? "crmImport.progress.toastFailed"
            : run.dryRun
              ? "crmImport.progress.toastDry"
              : "crmImport.progress.toastDone",
        ),
      );
    },
    [load, t, toast],
  );

  const watch = useImportRun(activeRunId, onFinished);

  // "A run is in flight" covers the gap between pressing the button and the
  // first poll answer, where `watch.run` is still null and the buttons would
  // otherwise blink back to life for two seconds.
  const inFlight = activeRunId !== null && !watch.gaveUp && (watch.run === null || isRunActive(watch.run));
  const busy = starting || inFlight;
  const canRun = status?.configured === true && status.connection?.ok === true;

  function toggle(entity: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(entity);
      else next.delete(entity);
      return next;
    });
  }

  function selectAll(on: boolean) {
    const all = intro?.entities.filter((e) => e.table !== null).map((e) => e.entity) ?? [];
    setSelected(new Set(on ? all : []));
  }

  async function start(dryRun: boolean) {
    setStarting(true);
    try {
      const { runId } = await startImport({ entities: [...selected], dryRun });
      setActiveRunId(runId);
    } catch {
      toast(t("crmImport.actions.startError"));
    } finally {
      setStarting(false);
    }
  }

  if (loading) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  if (failed || !status) {
    return (
      <Card>
        <EmptyState
          icon={TriangleAlert}
          title={t("crmImport.loadErrorTitle")}
          body={t("crmImport.loadError")}
          action={
            <Btn tone="dark" onClick={() => void load()} disabled={refreshing}>
              {refreshing ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("crmImport.refresh")}
            </Btn>
          }
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <ConnectionCard status={status} intro={intro} busy={refreshing} onRefresh={() => void load()} />

      {/* Above the run button on purpose. The import can fill owners, but only
          if somebody has said who these people are - see StaffCard's own note. */}
      <StaffCard review={staff} onSaved={setStaff} />

      <EntityCard
        entities={intro?.entities ?? []}
        selected={selected}
        onToggle={toggle}
        onAll={selectAll}
        onRun={(dryRun) => void start(dryRun)}
        busy={busy}
        canRun={canRun}
      />

      <RunProgress watch={watch} />

      {intro ? <SkippedCard skipped={intro.skipped} /> : null}

      <RunHistory runs={runs} />

      {/* The undo sits below a real gap rather than four pixels under the
          history. Everything above adds rows; this removes them, and the only
          thing separating the two on screen is where the eye stops. */}
      <div className="pt-6">
        <RevertCard footprint={status.footprint} disabled={busy} onDone={() => void load()} />
      </div>
    </div>
  );
}
